// slothfulchat-web: live directory of public chatmail relays for the
// instant-onboarding relay picker.
//
// The list is fetched as JSON from an automated mirror of
// https://chatmail.at/relays (the site's markdown source repo is private and
// the site itself sends no CORS headers, so a browser app can't read either
// directly — the mirror re-publishes the list daily with CORS enabled).
// Operators can point elsewhere or disable the picker via
// SLOTHFUL_RELAY_DIRECTORY.
//
// Fetching the list is cheap and happens up front; probing each relay over the
// WS→TCP bridge is not, so it runs lazily — only once the user opens the picker
// (see RelayPicker.tsx). A relay the bridge can't even resolve can't be signed
// up on from this app, so it is offered but disabled; a reachable relay is also
// timed (TCP+TLS handshake to :993) for a rough latency hint. Everything fails
// soft: the default relay is always offered and never probed away.

import { getLogger } from '@deltachat-desktop/shared/logger'

import { DEFAULT_CHATMAIL_HOSTNAME } from './chatmailInstances'
import { parseRelayDirectory } from './relayDirectoryParse'
import { instanceDefaultChatmailHost } from '../../../utils/slothfulInstanceConfig'

const log = getLogger('renderer/WelcomeScreen/relayDirectory')

/** Default source of the public relay list: a dumb automated daily mirror of
 * https://chatmail.at/relays, served with CORS from GitHub raw. */
export const RELAY_DIRECTORY_URL =
  'https://raw.githubusercontent.com/experintellia/chatmail-relays-mirror/refs/heads/data/relays.json'

/**
 * `SLOTHFUL_RELAY_DIRECTORY` (baked into
 * `window.__slothfulConfig.relayDirectoryUrl`): unset/empty = the default
 * mirror above; a https URL = fetch the relay-directory JSON from there
 * (must be CORS-readable and allowed by main.html's connect-src, which
 * assemble/customize pin to this same value); `off` = no directory at all,
 * the picker never shows. Returns undefined when the picker is disabled.
 */
function relayDirectoryUrl(): string | undefined {
  const value = (window as any).__slothfulConfig?.relayDirectoryUrl
  if (typeof value !== 'string' || value.trim() === '') {
    return RELAY_DIRECTORY_URL
  }
  const trimmed = value.trim()
  if (trimmed.toLowerCase() === 'off') {
    return undefined
  }
  return trimmed
}

const FETCH_TIMEOUT_MS = 10_000
const DNS_PROBE_TIMEOUT_MS = 4_000
const LATENCY_PROBE_TIMEOUT_MS = 4_000
// The directory is a small hand-sized list; anything larger is a broken or
// hostile source, so cap the body to avoid OOMing the tab before the timeout
// bites (the real mirror is a few KB).
const MAX_DIRECTORY_BYTES = 512 * 1024

/** The relay the "create profile" button signs up on when the user doesn't
 * pick anything else: the operator-configured SLOTHFUL_DEFAULT_CHATMAIL relay,
 * falling back to upstream's default instance. */
export function defaultRelayHost(): string {
  return instanceDefaultChatmailHost() ?? DEFAULT_CHATMAIL_HOSTNAME
}

/** Resolve the WS→TCP bridge URL exactly like the web-app runtime does
 * (`?proxy=` > saved > per-instance default > localhost) — the picker must
 * probe through the same bridge the core will actually connect through. */
function resolveBridgeUrl(): string | undefined {
  try {
    const proxy =
      new URLSearchParams(window.location.search).get('proxy') ||
      window.localStorage.getItem('slothfulchat.proxyUrl') ||
      (window as any).__slothfulConfig?.defaultProxyUrl ||
      'ws://localhost:8641'
    return /^wss?:\/\//.test(proxy) ? proxy.replace(/\/+$/, '') : undefined
  } catch {
    return undefined
  }
}

/** IPs the bridge's /dns/{host} endpoint resolves for `host`, or `[]` on any
 * timeout, socket error or empty answer. */
function resolveRelayIps(
  host: string,
  timeoutMs = DNS_PROBE_TIMEOUT_MS
): Promise<string[]> {
  const bridge = resolveBridgeUrl()
  if (!bridge) return Promise.resolve([])
  return new Promise(resolve => {
    let ws: WebSocket
    try {
      ws = new WebSocket(`${bridge}/dns/${encodeURIComponent(host)}`)
    } catch {
      resolve([])
      return
    }
    let done = false
    const finish = (ips: string[]) => {
      if (done) return
      done = true
      clearTimeout(timer)
      try {
        ws.close()
      } catch {
        /* already closing */
      }
      resolve(ips)
    }
    const timer = setTimeout(() => finish([]), timeoutMs)
    ws.onmessage = event => {
      try {
        const ips = JSON.parse(String(event.data))
        finish(
          Array.isArray(ips) ? ips.filter(ip => typeof ip === 'string') : []
        )
      } catch {
        finish([])
      }
    }
    ws.onerror = () => finish([])
    ws.onclose = () => finish([])
  })
}

// Port to probe for reachability + latency. Deliberately the plaintext IMAP
// port (143), NOT IMAPS (993): on 993 the server stays silent until the client
// sends a TLS ClientHello, so a passive "wait for the first byte" probe there
// times out on every relay. On 143 the IMAP server sends a plaintext greeting
// (`* OK …`, RFC 3501) the instant the TCP connection is up — the round trip we
// want — and chatmail relays (Dovecot) all listen on it. We only read the
// greeting and close: no credentials, no STARTTLS.
const IMAP_PROBE_PORT = 143

/** Outcome of the IMAP reachability probe: `{ ms }` when the relay's IMAP port
 * greeted us (round trip in ms), or `'unreachable'` when it didn't. The probe
 * runs over the *same* bridge the core signs up through, so anything it can't
 * reach the signup can't reach either: a relay whose IMAP port refuses/times
 * out, and a relay the bridge's allowlist forbids (a hosted bridge closes with
 * 4003 for any host it isn't configured to route to — the real signup gets the
 * same 4003) both mean "can't use this relay here", not a soft "couldn't
 * measure". */
type HandshakeResult = { ms: number } | 'unreachable'

function tcpHandshake(
  ip: string,
  timeoutMs = LATENCY_PROBE_TIMEOUT_MS
): Promise<HandshakeResult> {
  const bridge = resolveBridgeUrl()
  if (!bridge) return Promise.resolve('unreachable')
  return new Promise(resolve => {
    const start =
      typeof performance !== 'undefined' ? performance.now() : Date.now()
    let ws: WebSocket
    try {
      ws = new WebSocket(
        `${bridge}/tcp/${encodeURIComponent(ip)}/${IMAP_PROBE_PORT}`
      )
      ws.binaryType = 'arraybuffer'
    } catch {
      resolve('unreachable')
      return
    }
    let done = false
    const finish = (result: HandshakeResult) => {
      if (done) return
      done = true
      clearTimeout(timer)
      try {
        ws.close()
      } catch {
        /* already closing */
      }
      resolve(result)
    }
    const timer = setTimeout(() => finish('unreachable'), timeoutMs)
    // The bridge accepts the WS immediately; the first *message* is the IMAP
    // server's plaintext greeting, so its arrival ≈ the TCP round trip.
    ws.onmessage = () => {
      const now =
        typeof performance !== 'undefined' ? performance.now() : Date.now()
      finish({ ms: Math.max(0, Math.round(now - start)) })
    }
    // Any close/error before a message means we couldn't reach the relay's IMAP
    // port through the bridge — a down relay, or one the bridge's allowlist
    // forbids (4003) — and the real signup would fail the same way. onerror is a
    // no-op; onclose always follows it.
    ws.onerror = () => {}
    ws.onclose = () => finish('unreachable')
  })
}

/** Whether a relay can be reached over the bridge the core will sign up
 * through. `reachable` carries the round-trip `latencyMs`; `unreachable` means
 * DNS didn't resolve, or the relay's IMAP port (or the bridge's allowlist)
 * refused the connection. */
export type RelayStatus = 'reachable' | 'unreachable'

export type RelayProbe = {
  status: RelayStatus
  /** round-trip latency in ms, set only when `status === 'reachable'` */
  latencyMs: number | null
}

/**
 * Probe one relay over the bridge: resolve it, then (over the same bridge the
 * core will use) actually open its IMAP port to see whether it answers and how
 * fast. Never rejects — every failure maps to `unreachable`, never a success.
 */
export async function probeRelay(host: string): Promise<RelayProbe> {
  try {
    const ips = await resolveRelayIps(host)
    if (ips.length === 0) {
      return { status: 'unreachable', latencyMs: null }
    }
    const ipv4 = ips.find(ip => /^\d{1,3}(\.\d{1,3}){3}$/.test(ip))
    // No IPv4 to open the raw /tcp tunnel to (the endpoint addresses IPv4
    // literals), so we can't confirm the relay is reachable — don't claim it is.
    if (!ipv4) {
      return { status: 'unreachable', latencyMs: null }
    }
    const handshake = await tcpHandshake(ipv4)
    return typeof handshake === 'object'
      ? { status: 'reachable', latencyMs: handshake.ms }
      : { status: 'unreachable', latencyMs: null }
  } catch {
    return { status: 'unreachable', latencyMs: null }
  }
}

async function loadRelayDirectory(): Promise<string[]> {
  const defaultHost = defaultRelayHost()
  const url = relayDirectoryUrl()
  if (url === undefined) {
    // picker disabled by the operator (SLOTHFUL_RELAY_DIRECTORY=off)
    return [defaultHost]
  }
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  let directory: unknown
  try {
    const response = await fetch(url, {
      signal: controller.signal,
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    const declaredLength = Number(response.headers.get('content-length'))
    if (declaredLength > MAX_DIRECTORY_BYTES) {
      throw new Error(`directory too large (${declaredLength} bytes)`)
    }
    // parse explicitly (GitHub raw serves the file as text/plain; the
    // content-type never matters this way). Guard the body size too — a
    // missing/lying Content-Length shouldn't let an unbounded body through.
    const text = await response.text()
    if (text.length > MAX_DIRECTORY_BYTES) {
      throw new Error(`directory too large (${text.length} bytes)`)
    }
    directory = JSON.parse(text)
  } finally {
    clearTimeout(timer)
  }

  // The default relay is always offered and listed first; the directory
  // supplies the rest, in its own order, minus a duplicate of the default.
  const others = parseRelayDirectory(directory).filter(
    host => host !== defaultHost
  )
  return [defaultHost, ...others]
}

let cached: Promise<string[]> | undefined

/**
 * The relays the user may pick from: the default relay first, then every relay
 * in the directory, in directory order. This is the cheap step (one fetch, no
 * probing) that decides whether the picker is worth showing at all — a
 * single-entry result means "nothing to choose". Cached per page load; a failed
 * fetch is retried next time (the network may have come up meanwhile).
 */
export function fetchRelayDirectory(): Promise<string[]> {
  if (!cached) {
    cached = loadRelayDirectory().catch(error => {
      cached = undefined
      log.warn('relay directory unavailable, keeping the default relay', error)
      return [defaultRelayHost()]
    })
  }
  return cached
}
